import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, Brain, Shield, Eye, Clock, Sparkles, ChevronRight, Zap } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    { icon: Brain, title: "AI-Powered Reflection", desc: "AI turns your context into vivid alternative futures, while clearly separating your facts from model assumptions." },
    { icon: Eye, title: "Vivid Future Memories", desc: "Not graphs. Immersive narratives: 'It is 3 years later. You chose Option B. Here's what Tuesday feels like.'" },
    { icon: Shield, title: "Never a Recommendation", desc: "A mirror, not a decision-maker. The Regret Engine surfaces trade-offs. You make the call." },
    { icon: Clock, title: "7 Decision Modes", desc: "Career, Education, Relationship, Health, Financial, Custom, and Daily Chores with tailored analysis." },
  ];

  const steps = [
    { num: "01", title: "Describe Your Decision", desc: "Tell us what you're facing. Our AI helper guides you if you're unsure." },
    { num: "02", title: "Configure Your Lens", desc: "Set your risk tolerance and time horizon. We explain exactly how each affects results." },
    { num: "03", title: "Receive Future Memories", desc: "Get 6 vivid scenarios (optimistic, realistic, pessimistic) for each option." },
    { num: "04", title: "Examine the Trade-off", desc: "Compare qualitative regret outlooks, assumptions, and the unknowns that could change your decision." },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 glass-heavy" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Button data-testid="go-to-dashboard-btn" onClick={() => navigate('/dashboard')} className="rounded-full px-6 py-2 text-sm font-semibold" style={{ background: '#6366F1' }}>
                Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <>
                <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate('/auth')} className="text-gray-400 hover:text-white text-sm">
                  Sign In
                </Button>
                <Button data-testid="nav-signup-btn" onClick={() => navigate('/auth?mode=register')} className="rounded-full px-6 py-2 text-sm font-semibold" style={{ background: '#6366F1' }}>
                  Get Started <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center hero-overlay" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1771622218492-056844b21cec?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Contemplation" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(5,5,5,0.7), rgba(5,5,5,1))' }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 pt-32 pb-20">
          <div className="animate-fadeInUp opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            <p className="text-xs uppercase tracking-[0.3em] mb-6" style={{ color: '#6366F1', fontFamily: 'Manrope' }}>
              Pre-Decision Simulation Platform
            </p>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mb-8 animate-fadeInUp opacity-0" 
              style={{ fontFamily: 'Syne', animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            Rehearse Your<br />
            <span className="text-glow" style={{ color: '#6366F1' }}>Future</span> Before<br />
            You Choose It
          </h1>
          <p className="text-base md:text-lg max-w-xl mb-12 leading-relaxed animate-fadeInUp opacity-0" 
             style={{ color: '#94A3B8', fontFamily: 'Manrope', animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            Important choices can hide assumptions, trade-offs, and uncomfortable possibilities.
            Regret Engine creates illustrative scenarios so you can examine both paths before acting.
          </p>
          <div className="flex flex-wrap gap-4 animate-fadeInUp opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            <Button data-testid="hero-cta-btn" onClick={() => navigate(user ? '/decision/new' : '/auth?mode=register')}
              className="rounded-full px-8 py-6 text-base font-bold glow-primary-hover transition-transform hover:-translate-y-1"
              style={{ background: '#6366F1', boxShadow: '0 0 20px rgba(99,102,241,0.4)' }}>
              <Sparkles className="w-5 h-5 mr-2" /> Start Your First Simulation
            </Button>
            <Button data-testid="hero-learn-btn" variant="outline" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-full px-8 py-6 text-base border-white/20 hover:bg-white/5">
              See How It Works <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <p className="mt-8 text-xs animate-fadeInUp opacity-0" style={{ color: '#475569', animationDelay: '0.5s', animationFillMode: 'forwards' }}>
            First simulation free. No credit card required.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-32 px-6" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: '#6366F1' }}>What Makes It Different</p>
          <h2 className="text-base md:text-lg font-semibold mb-16 max-w-md" style={{ fontFamily: 'Syne', color: '#F8FAFC' }}>
            Not a pros-and-cons list. A rehearsal studio for your life.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <div key={i} className="glass-card rounded-xl p-8 transition-colors hover:border-white/10" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-6" style={{ background: 'rgba(99,102,241,0.15)' }}>
                  <f.icon className="w-6 h-6" style={{ color: '#6366F1' }} />
                </div>
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Syne' }}>{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-32 px-6 relative" data-testid="how-it-works-section">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1763633924639-57e537993d8e?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Misty path" 
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0" style={{ background: '#050505', opacity: 0.85 }} />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: '#6366F1' }}>The Process</p>
          <h2 className="text-base md:text-lg font-semibold mb-16" style={{ fontFamily: 'Syne' }}>
            Four steps to clarity.
          </h2>
          <div className="space-y-12">
            {steps.map((s, i) => (
              <div key={i} className="flex gap-8 items-start" data-testid={`step-${i}`}>
                <div className="text-4xl font-bold shrink-0 w-16" style={{ fontFamily: 'Syne', color: 'rgba(99,102,241,0.3)' }}>{s.num}</div>
                <div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Syne' }}>{s.title}</h3>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-32 px-6" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: '#6366F1' }}>Pricing</p>
          <h2 className="text-base md:text-lg font-semibold mb-16" style={{ fontFamily: 'Syne' }}>
            Pay for important decisions, not another subscription.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="glass-card rounded-xl p-8" data-testid="pricing-free">
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Syne' }}>Free</h3>
              <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Syne' }}>$0</p>
              <p className="text-sm mb-6" style={{ color: '#475569' }}>1 simulation included</p>
              <ul className="space-y-3 text-sm" style={{ color: '#94A3B8' }}>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> 1 full simulation</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> All 7 decision modes</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> AI chat helper</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> PDF report download</li>
              </ul>
            </div>
            <div className="glass-card rounded-xl p-8" data-testid="pricing-decision-pack">
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Syne' }}>Decision Pack</h3>
              <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Syne', color: '#6366F1' }}>$4.99</p>
              <p className="text-sm mb-6" style={{ color: '#475569' }}>one-time · 1 additional credit</p>
              <ul className="space-y-3 text-sm" style={{ color: '#94A3B8' }}>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> 1 full simulation</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> All decision modes</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> Scenario comparison</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> PDF report</li>
              </ul>
            </div>
            <div className="glass-card rounded-xl p-8 relative overflow-hidden" style={{ border: '1px solid rgba(99,102,241,0.3)' }} data-testid="pricing-decision-sprint">
              <div className="absolute top-0 right-0 px-3 py-1 text-xs font-semibold rounded-bl-lg" style={{ background: '#6366F1' }}>For several decisions</div>
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Syne' }}>Decision Sprint</h3>
              <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Syne', color: '#6366F1' }}>$14.99</p>
              <p className="text-sm mb-6" style={{ color: '#475569' }}>one-time · 4 simulation credits</p>
              <ul className="space-y-3 text-sm" style={{ color: '#94A3B8' }}>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> 25% less per credit than a single pack</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> All decision modes</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> Use credits whenever needed</li>
                <li className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} /> PDF reports</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center" data-testid="cta-section">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: 'Syne' }}>
            Stop imagining. Start <span style={{ color: '#6366F1' }}>rehearsing</span>.
          </h2>
          <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
            Your first simulation is free. Examine both paths before you choose.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button data-testid="cta-btn" onClick={() => navigate(user ? '/decision/new' : '/auth?mode=register')}
              className="rounded-full px-10 py-6 text-base font-bold glow-primary-hover transition-transform hover:-translate-y-1"
              style={{ background: '#6366F1', boxShadow: '0 0 20px rgba(99,102,241,0.4)' }}>
              Begin Now <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <button onClick={() => navigate('/why')}
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-medium transition-transform hover:-translate-y-1"
              style={{ border: '1px solid rgba(99,102,241,0.4)', color: '#6366F1' }}>
              Why this approach
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12 px-6" style={{ borderColor: 'rgba(255,255,255,0.05)' }} data-testid="footer">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" style={{ color: '#6366F1' }} />
              <span className="text-sm font-semibold" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
              <span className="text-xs" style={{ color: '#475569' }}>by SolvAI Labs</span>
            </div>
            <div className="flex items-center gap-6">
              <button onClick={() => navigate('/why')} className="text-xs hover:underline" style={{ color: '#94A3B8' }}>Why Regret Engine</button>
              <button onClick={() => navigate('/privacy')} className="text-xs hover:underline" style={{ color: '#94A3B8' }}>Privacy Policy</button>
              <button onClick={() => navigate('/terms')} className="text-xs hover:underline" style={{ color: '#94A3B8' }}>Terms of Service</button>
              <a href="mailto:ritik@solvailabs.com" className="text-xs hover:underline" style={{ color: '#94A3B8' }}>Contact</a>
              <a href="https://solvailabs.com" target="_blank" rel="noopener noreferrer" className="text-xs hover:underline" style={{ color: '#94A3B8' }}>SolvAI Labs</a>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs" style={{ color: '#475569' }}>
              This is a simulation tool, not a decision-maker. Always consult professionals for medical, legal, or financial advice.
            </p>
            <p className="text-xs" style={{ color: '#475569' }}>
              © 2026 SolvAI Labs. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
